/**
 * JobStatsPanel — the Job Details right column: application-progression stats and
 * a per-source breakdown of where candidates came from.
 *
 * Derived client-side from the job's candidates array (status counts, source
 * split) plus the tracked public-link channel stats (useCvSourceStats). The
 * Application Link (cv_upload) source can be decomposed into its per-channel
 * origins (LinkedIn, Indeed, …, Direct), kept collapsed to avoid clutter.
 */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Users,
  Clock,
  ListFilter,
  CalendarClock,
  ClipboardCheck,
  UserCheck,
  BadgeCheck,
  UserX,
} from 'lucide-react';

import { Candidate, JobOffer } from '../../models/job';
import { CandidateStatus } from '../../models/types';
import { getCandidateStatusCounts } from '../../utils/candidate-status';
import { useCvSourceStats, useJobPipeline } from '../../hooks/useJobOffers';
import { useStageTypeDisplayLabel } from '../../hooks/useStageDisplayLabel';

interface JobStatsPanelProps {
  job: JobOffer;
  candidates: Candidate[];
}

const CHANNEL_LABELS: Record<string, string> = {
  linkedin: 'LinkedIn',
  indeed: 'Indeed',
  website: 'Website',
};
const UNTAGGED = '__untagged__';

/** Ordered pipeline + its small, neutral tile icon. */
const PIPELINE: { status: CandidateStatus; icon: React.ReactNode }[] = [
  { status: CandidateStatus.PENDING, icon: <Clock className="h-3 w-3" /> },
  { status: CandidateStatus.SHORTLISTED, icon: <ListFilter className="h-3 w-3" /> },
  { status: CandidateStatus.INTERVIEWING, icon: <CalendarClock className="h-3 w-3" /> },
  { status: CandidateStatus.INTERVIEWED, icon: <ClipboardCheck className="h-3 w-3" /> },
  { status: CandidateStatus.ACCEPTED, icon: <UserCheck className="h-3 w-3" /> },
  { status: CandidateStatus.HIRED, icon: <BadgeCheck className="h-3 w-3" /> },
  { status: CandidateStatus.REJECTED, icon: <UserX className="h-3 w-3" /> },
];

const StatCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="overflow-hidden rounded-xl border border-light-lila/40 bg-light">
    <header className="flex h-11 items-center px-5 border-b border-light-lila/30">
      <h6 className="font-body text-xs font-semibold uppercase tracking-[0.07em] text-dark-invirtus/55">
        {title}
      </h6>
    </header>
    <div className="p-5">{children}</div>
  </section>
);

/** A compact stat tile: uppercase label + small neutral icon + big number. */
const Tile: React.FC<{
  label: string;
  value: number;
  icon: React.ReactNode;
  className?: string;
}> = ({ label, value, icon, className = '' }) => (
  <div className={`flex flex-col rounded-lg bg-background-gray/60 p-3 ${className}`}>
    <div className="mb-2 flex items-start justify-between gap-2">
      <span className="line-clamp-2 font-body text-[11px] font-medium uppercase leading-tight tracking-wide text-dark-invirtus/50">
        {label}
      </span>
      <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-dark-invirtus-soft text-dark-invirtus/40">
        {icon}
      </span>
    </div>
    <div className="mt-auto font-heading text-xl font-semibold leading-none tabular-nums text-dark-invirtus">
      {value}
    </div>
  </div>
);

export const JobStatsPanel: React.FC<JobStatsPanelProps> = ({ job, candidates }) => {
  const { t } = useTranslation('job');
  const { data: pipeline } = useJobPipeline(job.id);
  const getStageTypeLabel = useStageTypeDisplayLabel(pipeline?.stages);
  const list = Array.isArray(candidates) ? candidates : [];
  const total = list.length;

  const statusCounts = useMemo(() => getCandidateStatusCounts(list), [list]);
  const { data: channelStats = [] } = useCvSourceStats(job.id);

  const sourceCounts = useMemo(() => {
    const c: Record<string, number> = { cv_upload: 0, talent_pool: 0, talent_cloud: 0 };
    for (const cand of list) {
      const s = cand.source || 'cv_upload';
      if (c[s] !== undefined) c[s] += 1;
    }
    return c;
  }, [list]);

  const channelBreakdown = useMemo(() => {
    const tagged = [...channelStats].sort((a, b) => b.count - a.count);
    const taggedTotal = tagged.reduce((n, s) => n + s.count, 0);
    const direct = Math.max(0, sourceCounts.cv_upload - taggedTotal);
    return [...tagged, ...(direct > 0 ? [{ source: UNTAGGED, count: direct }] : [])];
  }, [channelStats, sourceCounts.cv_upload]);

  const pct = (n: number) => (total ? Math.round((n / total) * 100) : 0);

  // Lowest-granularity source breakdown: the Application Link (cv_upload) is
  // decomposed into its per-channel origins right here, so there is no aggregate
  // row left to expand. Talent Pool / Talent Cloud have no sub-channels.
  const sources = [
    ...channelBreakdown.map((c) => ({
      key: c.source,
      label:
        c.source === UNTAGGED
          ? t('overview.channel_direct')
          : CHANNEL_LABELS[c.source] ??
            c.source.charAt(0).toUpperCase() + c.source.slice(1),
      count: c.count,
    })),
    { key: 'talent_pool', label: t('table.source.talent_pool'), count: sourceCounts.talent_pool },
    { key: 'talent_cloud', label: t('table.source.talent_cloud'), count: sourceCounts.talent_cloud },
  ].filter((s) => s.count > 0);

  return (
    <div className="space-y-5">
      {/* Application progress — total + compact status tiles with a small icon each */}
      <StatCard title={t('overview.stats_pipeline')}>
        <div className="grid grid-cols-2 gap-2.5">
          {/* Total — same tile as the statuses, spanning the full width. */}
          <Tile
            label={t('overview.stats_total')}
            value={total}
            icon={<Users/>}
            className="col-span-2"
          />
          {PIPELINE.map(({ status, icon }) => (
            <Tile
              key={status}
              label={getStageTypeLabel(status)}
              value={statusCounts[status]}
              icon={icon}
            />
          ))}
        </div>
      </StatCard>

      {/* Sources — one row per lowest-granularity origin, no nested breakdown */}
      {total > 0 && (
        <StatCard title={t('overview.stats_sources')}>
          <ul className="space-y-2.5">
            {sources.map((s) => (
              <li key={s.key} className="flex items-center justify-between gap-2">
                <span className="font-body text-xs text-dark-invirtus/70">{s.label}</span>
                <span className="font-body text-xs tabular-nums">
                  <span className="font-semibold text-dark-invirtus">{s.count}</span>
                  <span className="ml-1 text-dark-invirtus/45">· {pct(s.count)}%</span>
                </span>
              </li>
            ))}
          </ul>
        </StatCard>
      )}
    </div>
  );
};

export default JobStatsPanel;
